import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { subscriptions } from '../api';
import { trackEvent } from '../lib/analytics';
import { trackActivity } from '../lib/activityEvents';
import { B } from '../constants';
import { useCurrency } from '../CurrencyContext';
import { transitions } from '../designTokens';

const tierConfig = {
  FREE: {
    label: 'Free',
    priceUsd: 0,
    gradient: 'linear-gradient(135deg,#2d2d2d,#1a1a1a)',
    badge: null,
    features: [
      'Basic trip planning',
      'Limited AI generations',
      'Public trip sharing',
      'Community access',
    ],
  },
  PRO: {
    label: 'Pro',
    priceUsd: 9.99,
    gradient: 'linear-gradient(135deg,#1a3a2a,#0d2818)',
    badge: { text: 'Most Popular', color: B.amber },
    features: [
      'Unlimited AI generations',
      'Advanced itinerary features',
      'Priority support',
      'Ad-free experience',
      'Custom travel guides',
    ],
  },
  CREATOR: {
    label: 'Creator',
    priceUsd: 29.99,
    gradient: 'linear-gradient(135deg,#3a1a5e,#1a0d28)',
    badge: { text: '✨ Best Value', color: '#8B5CF6' },
    features: [
      'Everything in Pro',
      'Creator profile page',
      'Monetization tools',
      'Analytics dashboard',
      '70% commission rate',
      'Featured listings',
    ],
  },
};

export default function SubscriptionScreen({ goScreen, user }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansData, currentData] = await Promise.all([
        subscriptions.getPlans(),
        subscriptions.getCurrent().catch(() => null),
      ]);
      setPlans(plansData.plans || []);
      setCurrent(currentData);
    } catch (err) {
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckout = async (tier) => {
    try {
      setProcessing(true);
      setError('');
      trackEvent('subscription_started', { tier });
      trackActivity('subscription', 'subscription_started', { plan: tier });
      const data = await subscriptions.checkout(tier);
      if (data.url) {
        window.location.href = data.url;
      } else if (data.sessionId) {
        const stripe = window.Stripe?.(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        if (stripe) {
          await stripe.redirectToCheckout({ sessionId: data.sessionId });
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    try {
      setProcessing(true);
      await subscriptions.cancel();
      alert('Subscription cancelled. You will retain access until the end of the billing period.');
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
        Loading subscription plans...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px 60px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ fontSize: 14, color: B.forest, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 10 }}>{t('subscription.pricing')}</div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 42, fontWeight: 700, marginBottom: 14, color: B.night }}>
          {t('subscription.chooseYourPlan')}
        </h1>
        <p style={{ color: B.muted, fontSize: 16, maxWidth: 440, margin: '0 auto', fontFamily: "'Montserrat',sans-serif" }}>
          {t('subscription.subtitle')}
        </p>
      </div>

      {error && (
        <div style={{ padding: 14, backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: 12, marginBottom: 24, fontSize: 14, fontFamily: "'Montserrat',sans-serif", textAlign: 'center' }}>
          {error}
        </div>
      )}

      {/* Current Subscription Banner */}
      {current?.user?.subscriptionTier && current.user.subscriptionTier !== 'FREE' && (
        <div style={{
          padding: '16px 24px',
          background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
          borderRadius: 16,
          marginBottom: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          border: '1px solid #86efac',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#166534', fontFamily: "'Montserrat',sans-serif" }}>
              ✓ {t('subscription.currentPlan')}: {tierConfig[current.user.subscriptionTier]?.label || current.user.subscriptionTier}
            </div>
            <div style={{ fontSize: 13, color: '#15803d', marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>
              {formatPrice(tierConfig[current.user.subscriptionTier]?.priceUsd || 0)} {t('subscription.perMonth')}
              {current.user.subscriptionExpiresAt && (
                <> • {t('subscription.expires')} {new Date(current.user.subscriptionExpiresAt).toLocaleDateString()}</>
              )}
            </div>
          </div>
          <button
            onClick={handleCancel}
            disabled={processing}
            style={{
              padding: '8px 20px',
              backgroundColor: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: processing ? 'not-allowed' : 'pointer',
              opacity: processing ? 0.6 : 1,
              fontWeight: 600,
              fontSize: 13,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {processing ? t('subscription.processing') : t('subscription.cancelSubscription')}
          </button>
        </div>
      )}

      {/* Plans Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 48 }}>
        {plans.map((plan) => {
          const config = tierConfig[plan.tier] || {};
          const isCurrent = current?.user?.subscriptionTier === plan.tier;
          const isFree = plan.tier === 'FREE';
          const isCreator = plan.tier === 'CREATOR';

          return (
            <div
              key={plan.tier}
              style={{
                padding: '32px 24px 28px',
                borderRadius: 20,
                background: config.gradient || '#1a1a1a',
                position: 'relative',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                transform: isCreator ? 'scale(1.05)' : 'scale(1)',
                boxShadow: isCreator
                  ? '0 8px 40px rgba(139,92,246,0.35)'
                  : '0 4px 20px rgba(0,0,0,0.15)',
                transition: `transform ${transitions.slow}, box-shadow ${transitions.slow}`,
                border: isCurrent ? `2px solid ${B.amber}` : '2px solid transparent',
              }}
              onMouseEnter={e => { if (!isCreator) e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={e => { if (!isCreator) e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {/* Badge */}
              {config.badge && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '5px 16px',
                  background: config.badge.color,
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'white',
                  fontFamily: "'Montserrat',sans-serif",
                  letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                }}>
                  {config.badge.text}
                </div>
              )}

              {/* Tier icon */}
              <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.9 }}>
                {isFree ? '🌱' : isCreator ? '⭐' : '🚀'}
              </div>

              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, margin: '0 0 4px 0' }}>
                {plan.name}
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: 36, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
                {formatPrice(config.priceUsd || 0)}
                <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.6 }}>{t('subscription.perMonth')}</span>
              </p>

              {/* Features */}
              <div style={{ flex: 1, marginBottom: 28 }}>
                {(config.features || []).map((feature) => (
                  <div key={feature} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', fontFamily: "'Montserrat',sans-serif", fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ color: '#4ade80', flexShrink: 0, marginTop: 1 }}>✓</span>
                    <span style={{ opacity: 0.85 }}>{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleCheckout(plan.tier)}
                disabled={isCurrent || isFree || processing}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  background: isCurrent
                    ? 'rgba(255,255,255,0.15)'
                    : 'white',
                  color: isCurrent ? 'white' : B.night,
                  border: 'none',
                  borderRadius: 12,
                  cursor: isCurrent || isFree ? 'default' : 'pointer',
                  opacity: isCurrent || isFree ? 0.6 : 1,
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: "'Montserrat',sans-serif",
                  transition: `all ${transitions.fast}`,
                }}
                onMouseEnter={e => { if (!isCurrent && !isFree) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; } }}
                onMouseLeave={e => { if (!isCurrent && !isFree) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; } }}
              >
                {isCurrent ? `✓ ${t('subscription.currentPlan')}` : isFree ? t('subscription.getStartedFree') : t('subscription.upgradeTo', { plan: config.label })}
              </button>
            </div>
          );
        })}
      </div>

      {/* Creator CTA */}
      {current?.user?.subscriptionTier !== 'CREATOR' && (
        <div style={{
          padding: '36px 32px',
          background: 'linear-gradient(135deg,#f5f0ff,#ede9fe)',
          borderRadius: 20,
          textAlign: 'center',
          border: '1px solid #ddd6fe',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🎙️</div>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, margin: '0 0 10px 0', color: B.night }}>
            {t('subscription.monetizeTitle')}
          </h3>
          <p style={{ margin: '0 auto 24px', color: B.muted, fontSize: 15, maxWidth: 480, fontFamily: "'Montserrat',sans-serif" }}>
            {t('subscription.monetizeSubtitle')}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleCheckout('CREATOR')}
              disabled={processing}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                cursor: processing ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "'Montserrat',sans-serif",
                opacity: processing ? 0.6 : 1,
              }}
            >
              {processing ? t('subscription.processing') : `${t('subscription.becomeCreator')} ✨`}
            </button>
            <button
              onClick={() => goScreen('reels')}
              style={{
                padding: '12px 28px',
                background: 'white',
                color: B.night,
                border: '1.5px solid #ddd6fe',
                borderRadius: 12,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {t('subscription.viewSampleReels')} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
